import React from 'react'
import ReactECharts from 'echarts-for-react';
import { format } from 'date-fns';

const HeadCircumferenceChart = (Props: any) => {
  // PROPS
  const {
    headCircumferenceList,
    headCircumferenceChartZoneData,
    isMobileScreen,
    isDownload
  } = Props;

  const currentHeadCircumference = headCircumferenceList
    ?.filter((item) => item?.headCircumference && item?.headCircumference > 0)  // <-- filter out 0
    ?.map(item => [
      new Date(item?.chartLabel)?.getTime(),
      item?.headCircumference
    ]);

  const maxWeightZoneValue = Math.max(
    headCircumferenceChartZoneData?.reduce((curr, item) => {
      return Math.max(curr, item.zoneUpperOrangeMaxValue ?? 0);
    }, 0) ?? 0,
  );

  const yAxisMaxPoint = Math.ceil(maxWeightZoneValue / 5) * 5;

  const hasData = currentHeadCircumference?.length > 0;

  if (!hasData) {
    return null;   // hide chart if no data
  }

  // Convert zone data to polygon format
  const createZonePolygon = (
    name: string,
    minKey: string,
    maxKey: string,
    color: string
  ) => {
    const maxPoints = headCircumferenceChartZoneData?.map(p => [
      new Date(p.ageDate).getTime(),
      p[maxKey]
    ]);
    const minPoints = headCircumferenceChartZoneData
      ?.map(p => [new Date(p.ageDate).getTime(), p[minKey]])
      .reverse();
    const polygonPoints = maxPoints.concat(minPoints);

    return {
      name,
      type: "line",
      data: polygonPoints,
      lineStyle: { width: 0 },
      symbol: "none",
      areaStyle: { color, opacity: 0.3 },
      smooth: true,
      tooltip: { show: false },
      z: 1,
    };
  };

  const color = {
    red: "#FF0000",
    orange: "#FFA500",
    yellow: "#FFFF00",
    green: "#008000",
  };

  const HeadCircumferenceOption = {
    title: {
      text: "Head Circumference Chart",
      left: "center",
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: "#fff",
      borderColor: "#ccc",
      borderWidth: 1,
      textStyle: {
        color: "#000",
        fontSize: 12,
      },
      formatter: function (params) {
        const dotColors = {
          "Current Head Circumference": "#000",  // Black
        };

        const firstPoint = params[0].value[0];
        const seriesData = headCircumferenceList.find(
          (x) => new Date(x.chartLabel).getTime() === firstPoint
        );

        const age = seriesData ? seriesData.age : null;

        let html = `<div style="text-align:left;">`;
        html += `<div style="font-weight:bold; margin-bottom:4px;">
          ${format(new Date(params[0].value[0]), "dd-MM-yyyy")}
          ${age ? `(${age})` : ""}
        </div>`;

        params.forEach(item => {
          const color = dotColors[item.seriesName] || item.color;
          html += `
            <div style="margin-bottom:2px;">
              <span style="width:8px; height:8px; border-radius:50%; background-color:${color}; display:inline-block; margin-right:6px;"></span>
              <span>${item.seriesName}: <strong>${item.value[1]}</strong> cm</span>
            </div>`;
        });

        html += `</div>`;
        return html;
      },
    },
    legend: {
      data: [
        {
          name: "1-3 Percentile",
          itemStyle: {
            color: "#FF0000"
          },
        },
        {
          name: "4-10 Percentile,",
          itemStyle: {
            color: "#FFA500"
          },
        },
        {
          name: "91-99 Percentile",
          icon: "none",
          textStyle: {
            padding: [0, 0, 0, -30]
          },
        },
        {
          name: "11-25 Percentile,",
          itemStyle: {
            color: "#FFFF00"
          },
        },
        {
          name: "76-90 Percentile",
          icon: "none",
          textStyle: {
            padding: [0, 0, 0, -30]
          }
        },
        {
          name: "26-75 Percentile",
          itemStyle: {
            color: "#008000"
          },
        },
        {
          name: "50 Percentile",
          itemStyle: {
            color: "#008000"
          },
        },
        {
          name: "26-75 Percentile",
          itemStyle: {
            color: "#008000"
          },
        },
        {
          name: "Current Head Circumference",
          itemStyle: {
            color: "#000"
          },
        },
      ],
      type: isDownload ? "plain" : "scroll",        // <-- enable scrolling if too many legends
      orient: "horizontal",   // horizontal layout
      top: isDownload ? 40 : 30,
      left: "center",
    },
    grid: {
      top: isDownload ? 120 : 85,
      bottom: 100
    },
    xAxis: {
      type: 'time',
      name: 'Age',
      nameLocation: "middle",
      nameGap: 30,
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: yAxisMaxPoint,
      name: "Head Circumference (cm)",
      nameLocation: "middle",
      nameRotate: 90,
      interval: Math.ceil(yAxisMaxPoint / 20),
      nameGap: 40,
    },
    dataZoom: [
      {
        type: 'inside',
        start: 0,
        end: 100,
      },
      {
        start: 0,
        end: 100
      }
    ],
    series: [
      // Zones as polygons
      createZonePolygon(
        '1-3 Percentile',
        'zoneLowerRedMinValue',
        'zoneLowerRedMaxValue',
        color.red
      ),
      createZonePolygon(
        '4-10 Percentile,',
        'zoneLowerOrangeMinValue',
        'zoneLowerOrangeMaxValue',
        color.orange
      ),
      createZonePolygon(
        '11-25 Percentile,',
        'zoneLowerYellowMinValue',
        'zoneLowerYellowMaxValue',
        color.yellow
      ),
      createZonePolygon(
        '26-75 Percentile',
        'zoneLowerGreenMinValue',
        'zoneLowerGreenMaxValue',
        color.green
      ),
      createZonePolygon(
        '26-75 Percentile',
        'zoneUpperGreenMinValue',
        'zoneUpperGreenMaxValue',
        color.green
      ),
      createZonePolygon(
        '76-90 Percentile',
        'zoneUpperYellowMinValue',
        'zoneUpperYellowMaxValue',
        color.yellow
      ),
      createZonePolygon(
        '91-99 Percentile',
        'zoneUpperOrangeMinValue',
        'zoneUpperOrangeMaxValue',
        color.orange
      ),
      createZonePolygon(
        '',
        'zoneUpperRedMinValue',
        'zoneUpperRedMaxValue',
        color.red
      ),

      // Center green dashed line
      {
        name: '50 Percentile',
        type: 'line',
        data: headCircumferenceChartZoneData.map(h => [
          new Date(h.ageDate).getTime(),
          h.zoneCenterGreenValue
        ]),
        lineStyle: {
          color: color.green,
          width: 1,
          type: 'solid'
        },
        showSymbol: false,
        tooltip: { show: false },
        z: 3,
      },
      {
        name: "Current Head Circumference",
        type: "line",
        data: currentHeadCircumference,
        lineStyle: {
          type: "solid",
          width: 2,
          color: "#000",
        },
        symbol: "circle",
        itemStyle: {
          color: '#000',
        },
        symbolSize: 6,
        z: 4,
        smooth: true,
      },
    ],
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
      }}
    >
      <ReactECharts
        option={HeadCircumferenceOption}
        style={{
          width: "100%",
          height: isDownload ? "750px" : isMobileScreen ? "60vh" : "1000px",  // <-- mobile vs full screen
        }}
        notMerge={true}
        lazyUpdate={true}
      />
    </div>
  )
}

export default HeadCircumferenceChart
